import { createSelector } from 'reselect'
import { NlpProviderMetadata } from '@feedyou/schema'

import { BotHeaderTab, BotHeaderTabType } from './types'
import { loadedBotApiUrlSelector, openHeaderTabsSelector } from '../../store/app/selectors'
import { mainDialogsSelector } from '../../store/flow/selectors/mainDialogsSelector'
import { createDialogTab, createNlpModelTab, createKnowledgeBaseTab, isTabOfType } from './utils'
import { nlpModelsSelector, knowledgeBasesSelector } from '../../store/botNlp'
import { featureValueSelector } from '../../store/features/selectors'
import { accessLevelSelector } from '../../store/user'
import { allAiAndLlmInvokeStepsSelector, areNlpChatQueriesEnabledSelector } from '../nlpQueries'

export const topHeaderTabsSelector = createSelector(
  [
    mainDialogsSelector,
    openHeaderTabsSelector,
    nlpModelsSelector,
    knowledgeBasesSelector,
    loadedBotApiUrlSelector,
    accessLevelSelector,
    featureValueSelector('NLP_EDITOR'),
    featureValueSelector('STEP_NLP_DESIGNER'),
    featureValueSelector('STANDALONE_KNOWLEDGE_BASE'),
    allAiAndLlmInvokeStepsSelector,
    areNlpChatQueriesEnabledSelector
  ],
  (
    mainDialogs,
    openHeaderTabs,
    nlpModels,
    knowledgeBases,
    apiUrl,
    accessLevel,
    isNlpEditorEnabled,
    isStepNlpDesignerEnabled,
    isStandaloneKbEnabled,
    aiAndLlmInvokeSteps,
    areNlpChatQueriesEnabled
  ): BotHeaderTab[] => {
    if (!accessLevel.view) return []
    const tabsForMainDialogs = mainDialogs.map(({ id }) => createDialogTab(id))
    const tabsForOpenClosableDialogs = openHeaderTabs.filter(isTabOfType(BotHeaderTabType.Dialog))

    const tabsForStepNlpModels = openHeaderTabs
      .filter(isTabOfType(BotHeaderTabType.StepNlpModel))
      .filter(() => isStepNlpDesignerEnabled)

    const tabsForGlobalNlpModels = nlpModels
      .map(({ id, name }) => createNlpModelTab(id, name))
      .filter(() => isNlpEditorEnabled)

    const nlpKbOrChatProviders: NlpProviderMetadata['type'][] = ['kb-chat', 'openai-chat']
    const nlpKbOrChatModels = nlpModels
      .filter(({ provider }) => nlpKbOrChatProviders.includes(provider?.type))
      .filter(() => isNlpEditorEnabled)

    const tabsForKnowledgeBases = isStandaloneKbEnabled
      ? knowledgeBases.map(({ id, name }) => createKnowledgeBaseTab(id, name))
      : []

    const tabsForNlpTraining =
      !!accessLevel.copywrite && !!apiUrl && !!tabsForGlobalNlpModels.length
        ? [{ type: BotHeaderTabType.NlpTraining }]
        : []

    const areQueriesEnabled = !!areNlpChatQueriesEnabled && !!accessLevel.copywrite && !!apiUrl
    const existsStepsOrModelsForQueries = !!nlpKbOrChatModels.length || !!aiAndLlmInvokeSteps.length
    const tabsForNlpQueries =
      areQueriesEnabled && existsStepsOrModelsForQueries
        ? [{ type: BotHeaderTabType.NlpChatQueries }]
        : []

    return [
      ...[...tabsForMainDialogs, ...tabsForOpenClosableDialogs],
      ...[...tabsForStepNlpModels, ...tabsForGlobalNlpModels],
      ...[...tabsForKnowledgeBases],
      ...[...tabsForNlpTraining, ...tabsForNlpQueries]
    ]
  }
)
